import { toast } from "react-toastify";
import { Button, Modal, Popconfirm, Switch, Table, Tabs, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PERM } from "../../../auth/permissions";
import { useMe } from "../../../auth/MeContext";
import axiosInstance from "../../../axios/axios-instance";
import { RoleAuditLog, RoleSummary, RoleUser } from "../../../@type/role.types";
import ActionButton from "../../../components/common/buttons/ActionButton";
import CancelButton from "../../../components/common/buttons/CancelButton";
import Card from "../../../components/common/Card";
import { pathAPI } from "../../../utils/pathApi";
import RoleFormModal from "./RoleFormModal";

const ROW_BUTTON_MIN_WIDTH = 80;

const errorMessage = (e: unknown, fallback: string): string => {
  const err = e as { message?: string; response?: { data?: { message?: string } } };
  return err?.response?.data?.message || err?.message || fallback;
};

const AuditLogTable = () => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<RoleAuditLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    axiosInstance
      .get(pathAPI.roleAuditLogs, { params: { page, size: 20 } })
      .then((r) => {
        setRows(r.data?.data ?? []);
        setTotal(r.data?.total ?? 0);
      })
      .catch((e) => toast.error(errorMessage(e, t("roles.request_failed"))))
      .finally(() => setLoading(false));
  }, [page, t]);

  const columns: ColumnsType<RoleAuditLog> = [
    { title: t("roles.audit.time"), dataIndex: "created_date", render: (v: string) => moment(v).format("YYYY/MM/DD HH:mm:ss") },
    { title: t("roles.audit.action"), dataIndex: "action", render: (v: string) => <Tag>{v}</Tag> },
    { title: t("roles.audit.actor"), dataIndex: "actor_admin_id" },
    { title: t("roles.audit.role"), dataIndex: "role_id" },
    { title: t("roles.audit.target"), dataIndex: "target_admin_id" },
    { title: t("roles.audit.before"), dataIndex: "before_value", ellipsis: true },
    { title: t("roles.audit.after"), dataIndex: "after_value", ellipsis: true },
  ];

  return (
    <Table<RoleAuditLog>
      rowKey="id"
      size="small"
      loading={loading}
      columns={columns}
      dataSource={rows}
        pagination={{ current: page + 1, pageSize: 20, total, showSizeChanger: false, showTotal: (total, range) => t("roles.audit.range", { from: range[0], to: range[1], total }), onChange: (p) => setPage(p - 1) }}
    />
  );
};

const RoleManagement = () => {
  const { t } = useTranslation();
  const { can } = useMe();
  const canManage = can(PERM.ROLE_MANAGE);
  const [roles, setRoles] = useState<RoleSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [usersModal, setUsersModal] = useState<{ role: RoleSummary; users: RoleUser[] } | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    axiosInstance
      .get(pathAPI.roles)
      .then((r) => setRoles(r.data?.data ?? []))
      .catch((e) => toast.error(errorMessage(e, t("roles.request_failed"))))
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const openForm = (id: number | null) => {
    setEditingId(id);
    setFormOpen(true);
  };

  const toggleActive = (role: RoleSummary, active: boolean) =>
    axiosInstance
      .put(`${pathAPI.roles}/${role.id}/active`, { active })
      .then(load)
      .catch((e) => toast.error(errorMessage(e, t("roles.request_failed"))));

  const remove = (role: RoleSummary) =>
    axiosInstance
      .delete(`${pathAPI.roles}/${role.id}`)
      .then(() => {
        toast.success(t("roles.deleted"));
        load();
      })
      .catch((e) => toast.error(errorMessage(e, t("roles.request_failed"))));

  const showUsers = (role: RoleSummary) =>
    axiosInstance
      .get(`${pathAPI.roles}/${role.id}/users`)
      .then((r) => setUsersModal({ role, users: r.data?.data ?? [] }))
      .catch((e) => toast.error(errorMessage(e, t("roles.request_failed"))));

  const columns: ColumnsType<RoleSummary> = [
    {
      title: t("roles.col.role"),
      dataIndex: "name",
      render: (_: string, r) => (
        <div>
          <b>{r.name}</b> {r.is_system && <Tag color="purple">{t("roles.col.system")}</Tag>}
          <div className="text-xs" style={{ color: "var(--app-text-muted)" }}>{r.code}</div>
        </div>
      ),
    },
    {
      title: t("roles.col.scope"),
      dataIndex: "scope_type",
      render: (v: RoleSummary["scope_type"], r) => (
        <span>
          {t(`roles.scope.${v}`, { defaultValue: v })}
          {v === "COUNTRY" && (r.multi_country ? ` · ${t("roles.col.multi")}` : ` · ${t("roles.col.single")}`)}
        </span>
      ),
    },
    { title: t("roles.col.permissions"), dataIndex: "permission_count", align: "center" },
    {
      title: t("roles.col.users"),
      dataIndex: "user_count",
      align: "center",
      render: (v: number, r) => (
        <Button type="link" onClick={() => showUsers(r)} disabled={v === 0}>
          {v}
        </Button>
      ),
    },
    {
      title: t("roles.col.active"),
      dataIndex: "active",
      align: "center",
      render: (v: boolean, r) => (
        <Switch checked={v} disabled={!canManage || r.code === "SUPER_ADMIN"} onChange={(c) => toggleActive(r, c)} />
      ),
    },
    {
      title: t("roles.col.actions"),
      align: "center",
      render: (_: unknown, r) => (
        <div className="flex gap-2 justify-center">
          <ActionButton sx={{ minWidth: ROW_BUTTON_MIN_WIDTH }} onClick={() => openForm(r.id)}>
            {canManage && r.code !== "SUPER_ADMIN" ? t("roles.edit") : t("roles.view")}
          </ActionButton>
          {canManage && !r.is_system && (
            <Popconfirm title={t("roles.delete_confirm")} onConfirm={() => remove(r)} disabled={r.user_count > 0}>
              <span>
                <CancelButton sx={{ minWidth: ROW_BUTTON_MIN_WIDTH }} disabled={r.user_count > 0}>
                  {t("roles.delete")}
                </CancelButton>
              </span>
            </Popconfirm>
          )}
        </div>
      ),
    },
  ];

  return (
    <Card>
      <Tabs
        items={[
          {
            key: "roles",
            label: t("roles.tab.roles"),
            children: (
              <>
                {canManage && (
                  <div className="text-right mb-3">
                    <ActionButton appSize="large" onClick={() => openForm(null)}>
                      {t("roles.create")}
                    </ActionButton>
                  </div>
                )}
                <Table<RoleSummary> rowKey="id" loading={loading} columns={columns} dataSource={roles} pagination={false} />
              </>
            ),
          },
          { key: "audit", label: t("roles.tab.audit"), children: <AuditLogTable /> },
        ]}
      />
      <RoleFormModal
        open={formOpen}
        roleId={editingId}
        allRoles={roles}
        readOnly={!canManage}
        onClose={() => setFormOpen(false)}
        onSaved={() => {
          setFormOpen(false);
          load();
        }}
      />
      <Modal
        open={!!usersModal}
        title={usersModal ? t("roles.users_modal_title", { name: usersModal.role.name }) : ""}
        footer={null}
        onCancel={() => setUsersModal(null)}
      >
        {usersModal?.users.map((u) => (
          <div key={u.id} className="py-1 border-b">
            {u.full_name} — {u.email}
          </div>
        ))}
      </Modal>
    </Card>
  );
};

export default RoleManagement;
